import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, ICellRendererParams, ModuleRegistry, AllCommunityModule, themeQuartz } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-theme-material.css';
import '../styles/ag-grid-custom.css';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Autocomplete,
  TextField,
  CircularProgress,
} from '@mui/material';
import { FaSyncAlt, FaTrophy, FaExclamationTriangle, FaCheckCircle } from 'react-icons/fa';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import apiService from '../services/apiService';

ModuleRegistry.registerModules([AllCommunityModule]);

interface AuditProblem {
  matchId: number;
  matchOrderId: number | null;
  seriesId: string;
  seriesName: string;
  matchDesc: string;
  endDate: string;
  issues: string[];
  finalized: boolean;
  momCount: number;
  momManualOverride: boolean;
  contestsAffected: number;
  ageHours: number;
  innings?: {
    inningsId: number | null;
    team: string | null;
    runs: number;
    wickets: number;
    overs: number | null;
    estimated: boolean;
    batsmanCount: number;
  }[];
  resultText?: string;
}

interface AuditSummary {
  scanned: number;
  problemCount: number;
  unfinalized: number;
  missingMom: number;
  scorecardIncomplete: number;
  days: number;
  graceHours: number;
  problems: AuditProblem[];
}

interface MatchPlayer {
  id: number;
  name: string;
}

const ISSUE_LABEL: Record<string, string> = {
  unfinalized: 'Unfinalized',
  missing_mom: 'Missing MoM',
  scorecard_incomplete: 'Incomplete Scorecard',
};

const ISSUE_CLASS: Record<string, string> = {
  unfinalized: 'bg-red-100 text-red-700',
  missing_mom: 'bg-amber-100 text-amber-700',
  scorecard_incomplete: 'bg-purple-100 text-purple-700',
};

const ScoringHealth: React.FC = () => {
  const [data, setData] = useState<AuditSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState(14);
  const [rowBusy, setRowBusy] = useState<number | null>(null);

  // MoM dialog state
  const [momOpen, setMomOpen] = useState(false);
  const [momMatch, setMomMatch] = useState<AuditProblem | null>(null);
  const [momPlayers, setMomPlayers] = useState<MatchPlayer[]>([]);
  const [momSelected, setMomSelected] = useState<MatchPlayer[]>([]);
  const [momLoading, setMomLoading] = useState(false);
  const [momSaving, setMomSaving] = useState(false);

  const loadAudit = useCallback(() => {
    setLoading(true);
    apiService
      .getFinalizationAudit(days, 2)
      .then(setData)
      .catch((e) => toast.error(e?.message || 'Failed to load scoring health'))
      .finally(() => setLoading(false));
  }, [days]);

  useEffect(() => {
    loadAudit();
  }, [loadAudit]);

  const handleRefetch = useCallback(
    async (p: AuditProblem) => {
      if (!window.confirm(`Refetch Cricbuzz and rescore ${p.matchDesc}? This re-pulls the scorecard and recomputes all contest points for this match.`)) return;
      setRowBusy(p.matchId);
      try {
        const result = await apiService.refetchRescoreMatch(p.matchId);
        toast.success(`${p.matchDesc}: ${result?.state || 'done'} — ${result?.contestsScored ?? 0} contest(s) scored.`);
        loadAudit();
      } catch (e: any) {
        toast.error(e?.message || 'Refetch & rescore failed');
      } finally {
        setRowBusy(null);
      }
    },
    [loadAudit],
  );

  const openMomDialog = useCallback(async (p: AuditProblem) => {
    setMomMatch(p);
    setMomOpen(true);
    setMomPlayers([]);
    setMomSelected([]);
    setMomLoading(true);
    try {
      const res = await apiService.getMatchPlayers(p.matchId);
      setMomPlayers(res.players || []);
      setMomSelected(res.currentMom || []);
    } catch (e: any) {
      toast.error(e?.message || 'Failed to load match players');
    } finally {
      setMomLoading(false);
    }
  }, []);

  const submitMom = useCallback(async () => {
    if (!momMatch) return;
    if (momSelected.length === 0) {
      toast.error('Select at least one Man of the Match');
      return;
    }
    if (!window.confirm(`Set MoM for ${momMatch.matchDesc} to ${momSelected.map((p) => p.name).join(', ')} and rescore? This overrides Cricbuzz and won't be wiped by future refetches.`)) return;
    setMomSaving(true);
    try {
      const result = await apiService.setMatchMom(momMatch.matchId, momSelected.map((p) => p.id));
      toast.success(`MoM set for ${momMatch.matchDesc} — ${result?.contestsRescored ?? 0} contest(s) rescored.`);
      setMomOpen(false);
      loadAudit();
    } catch (e: any) {
      toast.error(e?.message || 'Set MoM failed');
    } finally {
      setMomSaving(false);
    }
  }, [momMatch, momSelected, loadAudit]);

  const columnDefs = useMemo<ColDef<AuditProblem>[]>(
    () => [
      {
        headerName: 'Match #',
        field: 'matchOrderId',
        width: 110,
        valueFormatter: (p) => (p.value ? `M${p.value}` : '—'),
      },
      { headerName: 'Match', field: 'matchDesc', flex: 1, minWidth: 140 },
      {
        headerName: 'Final Scores',
        field: 'innings',
        flex: 1.4,
        minWidth: 250,
        autoHeight: true,
        wrapText: true,
        sortable: false,
        filter: false,
        headerTooltip: 'Official innings total (runs/wickets in overs). "~est" means reconstructed off the bat for a match synced before the totals fix — Refetch & Rescore to get the official figure.',
        cellRenderer: (params: ICellRendererParams<AuditProblem>) => {
          const p = params.data!;
          const inns = p.innings || [];
          return (
            <div className="py-1 leading-tight">
              {inns.length === 0 ? (
                <span className="text-xs text-red-600 font-semibold">no scorecard</span>
              ) : (
                inns.map((i, idx) => (
                  <div key={i.inningsId ?? idx} className="text-xs">
                    <span className="font-semibold">{i.team || `Inn ${i.inningsId ?? idx + 1}`}:</span>{' '}
                    {i.runs}/{i.wickets}
                    {i.overs != null ? ` (${i.overs})` : ''}
                    {i.estimated && <span className="ml-1 text-amber-600">~est</span>}
                    {i.batsmanCount === 0 && (
                      <span className="ml-1 text-red-600 font-semibold">⚠ none</span>
                    )}
                  </div>
                ))
              )}
              {p.resultText && (
                <div className="text-[11px] text-gray-500 mt-0.5">{p.resultText}</div>
              )}
            </div>
          );
        },
      },
      { headerName: 'Series', field: 'seriesName', flex: 1, minWidth: 150 },
      {
        headerName: 'Issue',
        field: 'issues',
        minWidth: 200,
        cellRenderer: (params: ICellRendererParams<AuditProblem>) => (
          <span className="flex gap-1 items-center h-full">
            {(params.value as string[]).map((i) => (
              <span
                key={i}
                className={`px-2 py-0.5 rounded-full text-xs font-semibold ${ISSUE_CLASS[i] || 'bg-gray-100 text-gray-700'}`}
              >
                {ISSUE_LABEL[i] || i}
              </span>
            ))}
            {params.data?.momManualOverride && (
              <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">Manual MoM</span>
            )}
          </span>
        ),
      },
      { headerName: 'Age (h)', field: 'ageHours', width: 100 },
      { headerName: 'Contests', field: 'contestsAffected', width: 110 },
      {
        headerName: 'Actions',
        minWidth: 260,
        sortable: false,
        filter: false,
        cellRenderer: (params: ICellRendererParams<AuditProblem>) => {
          const p = params.data!;
          const busy = rowBusy === p.matchId;
          return (
            <span className="flex gap-2 items-center h-full">
              <button
                onClick={() => handleRefetch(p)}
                disabled={busy}
                className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {busy ? <FaSyncAlt className="animate-spin" /> : <FaSyncAlt />} Refetch &amp; Rescore
              </button>
              <button
                onClick={() => openMomDialog(p)}
                disabled={busy}
                className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FaTrophy /> Set MoM
              </button>
            </span>
          );
        },
      },
    ],
    [rowBusy, handleRefetch, openMomDialog],
  );

  const problems = data?.problems || [];

  return (
    <div className="p-6">
      <ToastContainer position="top-right" autoClose={5000} />

      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <FaExclamationTriangle className="text-amber-500" /> Scoring Health
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Completed matches that aren&apos;t fully scored — stranded by a failed Step Function or missing MoM.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600 dark:text-gray-300">Lookback</label>
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="border border-gray-300 dark:border-dark-400 rounded-md px-2 py-1 text-sm bg-white dark:bg-dark-500 dark:text-white"
          >
            <option value={7}>7 days</option>
            <option value={14}>14 days</option>
            <option value={30}>30 days</option>
            <option value={60}>60 days</option>
          </select>
          <button
            onClick={loadAudit}
            disabled={loading}
            className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-md bg-gray-100 dark:bg-dark-400 text-gray-700 dark:text-gray-200 hover:bg-gray-200 disabled:opacity-50"
          >
            {loading ? <FaSyncAlt className="animate-spin" /> : <FaSyncAlt />} Refresh
          </button>
        </div>
      </div>

      {/* Summary chips */}
      {data && (
        <div className="flex gap-3 mb-4 flex-wrap">
          <div className="px-4 py-2 rounded-lg bg-gray-50 dark:bg-dark-500 border border-gray-200 dark:border-dark-400">
            <div className="text-xs text-gray-500">Scanned</div>
            <div className="text-lg font-bold text-gray-900 dark:text-white">{data.scanned}</div>
          </div>
          <div className="px-4 py-2 rounded-lg bg-red-50 border border-red-200">
            <div className="text-xs text-red-600">Unfinalized</div>
            <div className="text-lg font-bold text-red-700">{data.unfinalized}</div>
          </div>
          <div className="px-4 py-2 rounded-lg bg-amber-50 border border-amber-200">
            <div className="text-xs text-amber-600">Missing MoM</div>
            <div className="text-lg font-bold text-amber-700">{data.missingMom}</div>
          </div>
          <div className="px-4 py-2 rounded-lg bg-purple-50 border border-purple-200">
            <div className="text-xs text-purple-600">Incomplete Scorecard</div>
            <div className="text-lg font-bold text-purple-700">{data.scorecardIncomplete}</div>
          </div>
        </div>
      )}

      {!loading && problems.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <FaCheckCircle className="text-green-500 text-5xl mb-3" />
          <p className="text-lg font-semibold text-gray-700 dark:text-gray-200">All clear</p>
          <p className="text-sm text-gray-500">
            Every completed match in the last {days} days is fully scored.
          </p>
        </div>
      ) : (
        <div className="ag-theme-material" style={{ height: 'calc(100vh - 380px)', minHeight: '360px', width: '100%' }}>
          <AgGridReact<AuditProblem>
            rowData={problems}
            columnDefs={columnDefs}
            theme={themeQuartz}
            defaultColDef={{ sortable: true, resizable: true, filter: true }}
            animateRows
          />
        </div>
      )}

      {/* Manual MoM dialog */}
      <Dialog open={momOpen} onClose={() => !momSaving && setMomOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Set Man of the Match — {momMatch?.matchDesc}</DialogTitle>
        <DialogContent>
          {momLoading ? (
            <div className="flex justify-center py-8">
              <CircularProgress size={28} />
            </div>
          ) : (
            <>
              <p className="text-sm text-gray-500 mb-3 mt-1">
                Pick the player(s) Cricbuzz didn&apos;t publish. This sets a manual override that survives future Cricbuzz refetches, then rescores all contests for this match.
              </p>
              <Autocomplete
                multiple
                options={momPlayers}
                value={momSelected}
                onChange={(_e, v) => setMomSelected(v)}
                getOptionLabel={(o) => o.name || String(o.id)}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                renderInput={(params) => (
                  <TextField {...params} label="Man of the Match" placeholder="Select player(s)" />
                )}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMomOpen(false)} disabled={momSaving}>
            Cancel
          </Button>
          <Button onClick={submitMom} variant="contained" disabled={momSaving || momLoading}>
            {momSaving ? 'Saving…' : 'Set MoM & Rescore'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ScoringHealth;
